import type { buildAdministration } from "@kontextwerk/control-web-frame/shared/logic/administration"
import { subviewConfig as district } from "../pages/Administration/subviews/District/config"
import { subviewConfig as customers } from "../pages/Administration/subviews/Customers/config"
import { subviewConfig as properties } from "../pages/Administration/subviews/Properties/config"

type Administration = ReturnType<typeof buildAdministration>

/** Keeps the registry entries connected without reintroducing a shared tabbed page. */
export const withDistrictNavigation = (administration: Administration): Administration => {
  const entries = administration.sections.flatMap((section) => section.items)
  const navigation = [district.id, customers.id, properties.id].flatMap((id) => {
    const entry = entries.find((item) => item.id === id)

    return entry ? [entry] : []
  })

  if (navigation.length < 2) return administration

  const replacements = new Map(
    administration.pages.flatMap((page) => {
      if (!navigation.some((item) => item.id === page.id && item.href === page.path)) return []

      return [
        [
          page,
          { ...page, appPresentation: { ...page.appPresentation, localNavigation: navigation } },
        ],
      ] as const
    }),
  )
  const decorate = (pages: Administration["pages"]) =>
    pages.map((page) => replacements.get(page) ?? page)

  return {
    ...administration,
    sections: navigation.some((item) => item.id === district.id)
      ? administration.sections
          .map((section) => ({
            ...section,
            items: section.items.filter(
              (item) => item.id !== customers.id && item.id !== properties.id,
            ),
          }))
          .filter((section) => section.items.length > 0)
      : administration.sections,
    pages: decorate(administration.pages),
    getPagesForLocation: (location, unavailableContent) =>
      decorate(administration.getPagesForLocation(location, unavailableContent)),
  }
}
